import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import React, {
  ChangeEvent,
  FC,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { Box, IconButton } from '@mui/material';
import { Device, DeviceFilterAttributes } from '@thingslog/repositories';
import { DeviceModel } from '@thingslog/repositories';
import { DeviceLanguage } from '@thingslog/repositories/src/device/Device';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DataGridHeader,
  DataGridProps,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridValueFormatterParams,
  StatusChip,
  StatusChipConfig,
} from '@thingslog/ui-components';
import { CreateOrUpdateDeviceProps, UpdateDeviceProps } from './models/CreateOrUpdateDeviceProps';
import { format } from 'date-fns';
import { WarrantyStatus } from '@thingslog/repositories/src/device/Warranty';

const InventoryTable: FC<InventoryTableProps> = ({
  devices,
  setFilterValue,
  setAttributes,
  filterValue,
  showDeleteIcons,
  onDeleteOrUnlinkDevice,
  onUpdateDevice,
  onRmaHistory,
  showRmaHistory,
  companyId,
  showUnlinkBtn,
  ...props
}: InventoryTableProps) => {
  const [activeAttribute, setActiveAttribute] = useState<DeviceFilterAttributes | null>(null);
  const [rows, setRows] = useState<InventoryTableRow[]>([]);
  const { t } = useTranslation();

  const warrantyStatusChipConfig: StatusChipConfig = {
    EXPIRED: { label: t('warranty_status_EXPIRED').toLocaleUpperCase(), color: 'error' },
    IN_WARRANTY: { label: t('warranty_status_IN_WARRANTY').toLocaleUpperCase(), color: 'info' },
    UNKNOWN: { label: t('warranty_status_UNKNOWN').toLocaleUpperCase(), color: 'warning' },
    EXTENDED_SUPPORT: {
      label: t('warranty_extended_support').toLocaleUpperCase(),
      color: 'default',
    },
  };

  useEffect(() => {
    let rows: InventoryTableRow[] = [];
    devices.forEach((device: Device) => {
      rows.push({
        description: device.description || '',
        name: device.name,
        deviceNumber: device.number,
        hwVersion: device.hwVersion,
        id: device.number,
        model: device.model,
        deviceIcon: device.deviceIcon,
        iconId: device.deviceIconDto?.id || 0,
        swVersion: device.swVersion,
        nomenclature: device.nomenclature,
        language: device.language,
        createdBy: device.createdBy,
        createdOn: device.createdOn,
        installDate: device.installDate,
        installWarrantyPeriodMonths: device.installWarrantyPeriodMonths,
        manufacturingDate: device.manufacturingDate,
        warrantyStatus: device.warranty?.status ?? 'UNKNOWN',
        warrantyPeriodMonths: device.warrantyPeriodMonths,
        extendedHardwareSupport: device.extendedHardwareSupport,
      });
    });
    setRows(rows);
  }, [devices]);

  const handleFilterValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value === '' || e.target.value === null) {
      setActiveAttribute(null);
      setAttributes(null);
      setFilterValue('');
    } else {
      setActiveAttribute(DeviceFilterAttributes[e.target.id]);
      setAttributes(DeviceFilterAttributes[e.target.id]);
      setFilterValue(e.target.value);
    }
  };

  return (
    <DataGrid
      rows={rows}
      columns={[
        {
          field: 'deviceNumber',
          headerName: t('devices_table_device_number'),
          flex: 1.2,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return (
              <DataGridHeader<DeviceFilterAttributes>
                headerName={params.colDef.headerName!}
                attribute={DeviceFilterAttributes.deviceNumber}
                activeAttribute={activeAttribute}
                filterValue={filterValue}
                onFilterValueChange={handleFilterValueChange}
              />
            );
          },
        },
        {
          field: 'name',
          headerName: t('devices_table_device_name'),
          flex: 1.5,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return (
              <DataGridHeader<DeviceFilterAttributes>
                headerName={params.colDef.headerName!}
                attribute={DeviceFilterAttributes.deviceName}
                activeAttribute={activeAttribute}
                filterValue={filterValue}
                onFilterValueChange={handleFilterValueChange}
              />
            );
          },
        },
        {
          field: 'hwVersion',
          headerName: t('device_inventory_hardware_version'),
          flex: 1,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return (
              <DataGridHeader<DeviceFilterAttributes>
                headerName={params.colDef.headerName!}
                attribute={DeviceFilterAttributes.hw_version}
                activeAttribute={activeAttribute}
                filterValue={filterValue}
                onFilterValueChange={handleFilterValueChange}
              />
            );
          },
        },
        {
          field: 'swVersion',
          headerName: t('device_inventory_software_version'),
          flex: 1.5,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return (
              <DataGridHeader<DeviceFilterAttributes>
                headerName={params.colDef.headerName!}
                attribute={DeviceFilterAttributes.sw_version}
                activeAttribute={activeAttribute}
                filterValue={filterValue}
                onFilterValueChange={handleFilterValueChange}
              />
            );
          },
        },
        {
          field: 'model',
          headerName: t('model'),
          flex: 1.2,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return (
              <DataGridHeader<DeviceFilterAttributes>
                headerName={params.colDef.headerName!}
                attribute={DeviceFilterAttributes.model}
                activeAttribute={activeAttribute}
                filterValue={filterValue}
                onFilterValueChange={handleFilterValueChange}
              />
            );
          },
        },
        {
          field: 'description',
          headerName: t('description'),
          flex: 1.5,
          minWidth: 100,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName!} />;
          },
        },
        {
          field: 'language',
          headerName: t('link_language'),
          flex: 0.8,
          minWidth: 100,
          valueFormatter: (params: GridValueFormatterParams<DeviceLanguage | null>): string =>
            params.value ?? '',
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName!} />;
          },
        },
        {
          field: 'manufacturingDate',
          headerName: t('manufacturing_date'),
          flex: 1.8,
          minWidth: 100,
          valueFormatter: (params: GridValueFormatterParams<string | null>): string => {
            return params.value ? format(new Date(params.value), 'dd MMM yyyy') : '';
          },
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName} />;
          },
        },
        {
          field: 'warrantyPeriodMonths',
          headerName: t('warranty_period_months'),
          flex: 1.8,
          minWidth: 150,
          sortable: false,
          valueFormatter: (params: GridValueFormatterParams<string | null>): string => {
            return params.value ? `${params.value} ${t('duration_months_abbr')}` : '';
          },
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName} />;
          },
        },
        {
          field: 'warrantyStatus',
          headerName: t('warranty_status'),
          flex: 1.8,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName} />;
          },
          renderCell: (params: GridRenderCellParams<WarrantyStatus>): ReactNode => {
            return <StatusChip value={params.value} config={warrantyStatusChipConfig} />;
          },
        },
        {
          field: 'createdBy',
          headerName: t('createdBy'),
          flex: 1.5,
          minWidth: 100,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName!} />;
          },
        },
        {
          field: 'createdOn',
          headerName: t('createdOn'),
          flex: 1.5,
          minWidth: 100,
          valueFormatter: (params: GridValueFormatterParams<string>): string => {
            if (params.value) {
              // Append 'Z' to interpret the string as UTC time. (BE sends/saves date in UTC)
              const dateUTC = new Date(params.value + 'Z');
              // return date that is converted to users local timezone
              return format(dateUTC, 'dd/MM/yyyy HH:mm');
            } else {
              return '';
            }
          },
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName!} />;
          },
        },
        {
          field: 'actions',
          headerName: t('device_inventory_actions'),
          flex: 0.8,
          minWidth: 150,
          sortable: false,
          renderHeader: (params: GridColumnHeaderParams): ReactNode => {
            return <DataGridHeader headerName={params.colDef.headerName!} />;
          },
          renderCell: (params: GridRenderCellParams): ReactElement => {
            return (
              <Box>
                <IconButton
                  onClick={(e: MouseEvent): void => {
                    e.stopPropagation();
                    onUpdateDevice({ type: 'UPDATE', ...params.row } as UpdateDeviceProps);
                  }}
                >
                  <EditIcon />
                </IconButton>
                {showRmaHistory && (
                  <IconButton
                    onClick={(e: MouseEvent): void => {
                      e.stopPropagation();
                      onRmaHistory(params.row.deviceNumber);
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                )}
                {showDeleteIcons(params.row.model) && (
                  <IconButton
                    onClick={(e: MouseEvent): void => {
                      e.stopPropagation();
                      onDeleteOrUnlinkDevice(params.id.toString(), companyId, showUnlinkBtn);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          },
        },
      ]}
      {...props}
    />
  );
};

interface InventoryTableProps extends Omit<DataGridProps, 'columns' | 'rows'> {
  devices: Device[];
  filterValue: string;
  showDeleteIcons(model: DeviceModel): boolean;
  setAttributes: (attribute: DeviceFilterAttributes | null) => void;
  setFilterValue: (filterValue: string) => void;
  onDeleteOrUnlinkDevice: (
    selectedDevice: string,
    companyId: number | null,
    showUnlinkBtn: boolean
  ) => void;
  onUpdateDevice: (deviceData: CreateOrUpdateDeviceProps) => void;
  onRmaHistory: (deviceNumber: string) => void;
  showRmaHistory: boolean;
  companyId: number | null;
  showUnlinkBtn: boolean;
}

export interface InventoryTableRow {
  description: string;
  name: string;
  deviceNumber: string;
  hwVersion: string;
  id: string;
  model: DeviceModel;
  deviceIcon: string | null;
  iconId: number | null;
  swVersion: string;
  nomenclature: string | null;
  language: DeviceLanguage | null;
  createdBy: string | null;
  createdOn: string | null;
  installDate: string | null;
  installWarrantyPeriodMonths: number | null;
  manufacturingDate: string | null;
  warrantyStatus: WarrantyStatus;
  extendedHardwareSupport: boolean;
  warrantyPeriodMonths: number | null;
}

export default InventoryTable;
